import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { ReturnTypeEnum } from "../../return";
import { CompensationMethod } from "./filters/compensation-method-filter";
import { SalesChannelValues } from "./filters/sales-channel-values";
import { ViewByEnum } from "./filters/view-by-toggle";

/**
 * Schema for return analytics series data
 * This schema defines the data structure for time-series analytics
 */

// Input schema for return analytics series data
export const ReturnAnalyticsSeriesDataInputSchema = z.object({
  startDate: z
    .string()
    .describe("Start date for the analytics period (YYYY-MM-DD)"),
  endDate: z
    .string()
    .describe("End date for the analytics period (YYYY-MM-DD)"),
  types: z
    .array(createEnum(ReturnTypeEnum))
    .describe("Types of returns to include in the analytics"),
  salesChannels: z
    .array(z.enum(SalesChannelValues))
    .optional()
    .describe("Sales channels to filter by (optional)"),
  returnStatuses: z
    .array(z.string())
    .optional()
    .describe(
      "Return statuses to filter by. If omitted, all statuses will be included.",
    ),
  compensationMethods: z
    .array(createEnum(CompensationMethod))
    .optional()
    .describe("Compensation methods to filter by (optional)"),
  viewBy: createEnum([
    ViewByEnum.ORDER_DATE,
    ViewByEnum.PROCESSED_DATE,
    ViewByEnum.SUBMITTED_DATE,
  ])
    .default(ViewByEnum.ORDER_DATE)
    .describe(
      "Whether to view analytics by order date, submitted date, or processed date",
    ),
});

// Data point schema for time-series data
export const ReturnAnalyticsDataPointSchema = z.object({
  date: z.string().describe("Date for this data point (YYYY-MM-DD)"),
  returnedOrdersRate: z
    .number()
    .describe("Percentage of orders that had returns on this date"),
  returnedItemsRate: z
    .number()
    .describe("Percentage of items that were returned on this date"),
  returnedRevenueRate: z
    .number()
    .describe("Percentage of revenue affected by returns on this date"),
  protectedOrdersRateTotal: z
    .number()
    .describe("Percentage of protected orders out of total orders"),
  protectedOrdersRateEligible: z
    .number()
    .describe("Percentage of protected orders out of eligible orders"),

  // Compensation method breakdown counts
  exchangeOnlyCount: z
    .number()
    .describe("Count of returns with exchange only compensation"),
  refundOnlyCount: z
    .number()
    .describe("Count of returns with refund only compensation"),
  storeCreditOnlyCount: z
    .number()
    .describe("Count of returns with store credit only compensation"),
  exchangeRefundCount: z
    .number()
    .describe("Count of returns with both exchange and refund compensation"),
  exchangeStoreCreditCount: z
    .number()
    .describe(
      "Count of returns with both exchange and store credit compensation",
    ),
  refundStoreCreditCount: z
    .number()
    .describe(
      "Count of returns with both refund and store credit compensation",
    ),
  allTypesCount: z
    .number()
    .describe(
      "Count of returns with all compensation types (exchange, refund, store credit)",
    ),

  // Return method breakdown counts
  shipBackCount: z
    .number()
    .describe("Count of returns using ship back return method"),
  greenReturnCount: z
    .number()
    .describe("Count of returns using green return method (keep item)"),
  packagePickupCount: z
    .number()
    .describe("Count of returns using package pickup return method"),
  inStoreDropoffCount: z
    .number()
    .describe("Count of returns using in-store dropoff return method"),
  errorReturnMethodCount: z
    .number()
    .describe("Count of returns with error/unknown return method"),

  // Shipping service type breakdown counts
  fedexEasyReturnCount: z
    .number()
    .describe("Count of returns using FedEx Easy Return shipping service"),
  internationalReturnsCount: z
    .number()
    .describe("Count of returns using international return services"),
  otherReturnMethodCount: z
    .number()
    .describe("Count of returns using other shipping service types"),
});

// Output schema for return analytics series data
export const ReturnAnalyticsSeriesDataOutputSchema = z.object({
  data: z
    .array(ReturnAnalyticsDataPointSchema)
    .describe(
      "Array of data points for day by day breakdown of return/claims analytics",
    ),
});

// Export type definitions for TypeScript
export type ReturnAnalyticsSeriesDataInput = z.infer<
  typeof ReturnAnalyticsSeriesDataInputSchema
>;
export type ReturnAnalyticsSeriesDataOutput = z.infer<
  typeof ReturnAnalyticsSeriesDataOutputSchema
>;
export type ReturnAnalyticsDataPoint = z.infer<
  typeof ReturnAnalyticsDataPointSchema
>;

// Define the entire schema for RPC usage
export const returnAnalyticsSeriesData = {
  name: "returnAnalyticsSeriesData" as const,
  input: ReturnAnalyticsSeriesDataInputSchema,
  output: ReturnAnalyticsSeriesDataOutputSchema,
};
